<script setup lang="ts">
import { computed, nextTick, onMounted, type PropType, ref, watch } from "vue";
import { DesignVersion } from "@/models/types";
import BcButton from "./bc-button.vue";

defineOptions({ name: "BcListBox" });

/** Specifies the two-way binding (selected item) for the list */
const modelValue = defineModel<any>({ default: undefined });

const props = defineProps({
  /** Allows item deletion */
  allowDelete: { type: Boolean, default: false },
  /** Allows changing order of items */
  allowOrder: { type: Boolean, default: false },
  /** Allows item selection */
  allowSelect: { type: Boolean, default: false },
  /** Allows unselection on Escape */
  allowUnselect: { type: Boolean, default: false },
  /**
   * Specifies whether the first item should be selected automatically
   * @type {boolean}
   */
  autoSelect: { type: Boolean, default: false },
  /** Sets a border around list container */
  bordered: { type: Boolean, default: false },
  /** Specifies custom check for disabled items */
  checkDisabled: { type: Function as PropType<(item: any) => boolean> },
  /** Specifies custom check for read-only items */
  checkReadonly: { type: Function as PropType<(item: any) => boolean> },
  /** Specifies custom check for inactive items */
  checkInactive: { type: Function as PropType<(item: any) => boolean> },
  /** Lowers the list size (removes item padding) */
  dense: { type: Boolean, default: false },
  /** Defines the list's style */
  design: {
    type: String as PropType<DesignVersion>,
    default: "6",
    validator: (value: any) => DesignVersion.isDesignVersion(value),
  },
  /** Disables the list for selection */
  disabled: { type: Boolean, default: false },
  /** Expands the list to the width of its parent container */
  fullWidth: { type: Boolean, default: false },
  /** Expands the list to the height of its parent container */
  fullHeight: { type: Boolean, default: false },
  /** Specifies custom calculation for item key */
  getKey: { type: Function as PropType<(item: any) => string> },
  /** Specifies custom calculation for item text */
  getText: { type: Function as PropType<(item: any) => string> },
  /** Sets a divider between the items */
  itemDivider: { type: Boolean, default: false },
  /** Specifies the disabled property (e.g. disabled) for disabel item selection if items are objects */
  itemDisabledProperty: { type: String },
  /** Specifies the inactive property (e.g. readonly) to make item inactive styled if items are objects */
  itemInactiveProperty: { type: String },
  /** Specifies the key property (e.g. ID) for identification if items are objects */
  itemKeyProperty: { type: String },
  /** Specifies the text property (e.g. name) for display if items are objects */
  itemTextProperty: { type: String },
  /** Specifies the read-only property (e.g. readonly) to make item read-only if items are objects */
  itemReadonlyProperty: { type: String },
  /** Specifies the list items */
  items: { required: true, type: Array as PropType<Array<any>> },
  /** Specifies the number of visible items */
  numberOfVisibleItems: { type: Number },
  /** Makes the list border rounded */
  rounded: { type: Boolean, default: false },
  /** Removes background color from hovered and selected items */
  outlined: { type: Boolean, default: false },
  /** Makes list read-only */
  readonly: { type: Boolean, default: false },
});

const emit = defineEmits<{
  (e: "delete", payload: any): void;
  (e: "select", payload: any): void;
  (e: "update:model-value", payload: any): void;
}>();

const selectedIdx = ref(-1);

const listBoxRef = ref<HTMLUListElement | null>(null);
const listItemsRef = ref<HTMLLIElement[]>([]);

const pak = computed<string>(() => {
  return "bc-controls-d" + props.design;
});

const selectedItem = computed<any>(() => {
  if (props.items && selectedIdx.value > -1 && selectedIdx.value < props.items.length) {
    return props.items[selectedIdx.value];
  }
  return null;
});

function getDisabled(item: any): boolean {
  if (props.checkDisabled) {
    const disabled = props.checkDisabled(item);
    if (typeof disabled === "boolean") {
      return disabled;
    } else {
      console.warn("Custom disabled check function is invalid.");
    }
  } else if (
    typeof item === "object" &&
    item !== null &&
    props.itemDisabledProperty &&
    props.itemDisabledProperty in item &&
    typeof item[props.itemDisabledProperty] === "boolean"
  ) {
    return item[props.itemDisabledProperty];
  }
  return false;
}

function getReadonly(item: any): boolean {
  if (props.checkReadonly) {
    const readonly = props.checkReadonly(item);
    if (typeof readonly === "boolean") {
      return readonly;
    } else {
      console.warn("Custom read-only check function is invalid.");
    }
  } else if (
    typeof item === "object" &&
    item !== null &&
    props.itemReadonlyProperty &&
    props.itemReadonlyProperty in item &&
    typeof item[props.itemReadonlyProperty] === "boolean"
  ) {
    return item[props.itemReadonlyProperty];
  }
  return false;
}

function getInactive(item: any): boolean {
  if (props.checkInactive) {
    const readonly = props.checkInactive(item);
    if (typeof readonly === "boolean") {
      return readonly;
    } else {
      console.warn("Custom inactive check function is invalid.");
    }
  } else if (
    typeof item === "object" &&
    item !== null &&
    props.itemInactiveProperty &&
    props.itemInactiveProperty in item &&
    typeof item[props.itemInactiveProperty] === "boolean"
  ) {
    return item[props.itemInactiveProperty];
  }
  return false;
}

function selectItem(idx: number): void {
  if (
    props.allowSelect &&
    !props.disabled &&
    idx >= 0 &&
    idx < props.items?.length &&
    !getDisabled(props.items[idx]) &&
    selectedIdx.value !== idx
  ) {
    selectedIdx.value = idx;
  }
}

function deleteItem(): void {
  if (
    props.allowSelect &&
    props.allowDelete &&
    !props.disabled &&
    props.items?.length &&
    !getDisabled(props.items[selectedIdx.value]) &&
    !props.readonly &&
    !getReadonly(props.items[selectedIdx.value])
  ) {
    emit("delete", props.items[selectedIdx.value]);
    var currentIdx = selectedIdx.value;
    selectedIdx.value = -1;
    nextTick(() => {
      props.items?.splice(currentIdx, 1);
      selectedIdx.value = currentIdx;
      if (selectedIdx.value === props.items?.length) {
        moveSelection(-1);
        if (selectedIdx.value === currentIdx) {
          selectedIdx.value = -1;
        }
      } else if (getDisabled(props.items[selectedIdx.value])) {
        moveSelection(1);
        if (selectedIdx.value === currentIdx) {
          moveSelection(-1);
        }
        if (selectedIdx.value === currentIdx) {
          selectedIdx.value = -1;
        }
      }
    });
  }
}

function changeOrder(event: MouseEvent, idx: number, direction: number): void {
  // prevent item selection
  event.stopImmediatePropagation();
  let item = props.items?.splice(idx, 1)[0];
  props.items?.splice(idx + direction, 0, item);
  nextTick(() => {
    if (props.allowSelect && selectedIdx.value === idx) {
      selectedIdx.value = idx + direction;
    }
  });
}

function moveSelection(direction: number): void {
  if (!props.allowSelect || !props.items || props.disabled) return;

  let newSelectedIdx = selectedIdx.value + direction;
  while (
    newSelectedIdx > -1 &&
    newSelectedIdx < props.items?.length &&
    getDisabled(props.items[newSelectedIdx])
  ) {
    newSelectedIdx += direction;
  }
  if (newSelectedIdx > -1 && newSelectedIdx < props.items?.length) {
    selectedIdx.value = newSelectedIdx;
  }
}

function removeSelection(): void {
  if (
    !props.allowSelect ||
    !props.allowUnselect ||
    props.disabled ||
    selectedIdx.value === -1
  )
    return;
  selectedIdx.value = -1;
}

function calculateKey(item: any): any {
  if (props.getKey) {
    return props.getKey(item);
  }
  if (
    typeof item === "object" &&
    item !== null &&
    props.itemKeyProperty &&
    props.itemKeyProperty in item
  ) {
    return item[props.itemKeyProperty];
  }
  return undefined;
}

function calculateText(item: any): string {
  if (props.getText) {
    return props.getText(item);
  }
  if (
    typeof item === "object" &&
    item !== null &&
    props.itemTextProperty &&
    props.itemTextProperty in item
  ) {
    return item[props.itemTextProperty]?.toString() ?? "";
  }
  return item?.toString() ?? "";
}

/** Checks item properties */
function checkItemProperties(): void {
  if (
    props.itemKeyProperty &&
    props.items?.some(
      (item) =>
        props.itemKeyProperty &&
        (typeof item !== "object" ||
          (typeof item === "object" && item !== null && !(props.itemKeyProperty in item)))
    )
  ) {
    console.error(
      `Item key property '${props.itemKeyProperty}' is not available for all items.`
    );
  }
  if (
    props.itemKeyProperty &&
    modelValue.value &&
    (typeof modelValue.value !== "object" ||
      (typeof modelValue.value === "object" &&
        modelValue.value !== null &&
        !(props.itemKeyProperty in modelValue.value)))
  ) {
    console.error(
      `Item key property '${props.itemKeyProperty}' is not available in the model.`
    );
  }

  if (
    !props.itemTextProperty &&
    !props.getText &&
    props.items?.some((item) => typeof item === "object" && item !== null)
  ) {
    console.error("Item text property is not defined for object items.");
  }
  if (
    !props.itemTextProperty &&
    !props.getText &&
    typeof modelValue.value === "object" &&
    modelValue.value !== null
  ) {
    console.error("Item text property is not defined for model.");
  }
  if (
    props.itemTextProperty &&
    props.items?.some(
      (item) =>
        props.itemTextProperty &&
        (typeof item !== "object" ||
          (typeof item === "object" &&
            item !== null &&
            !(props.itemTextProperty in item)))
    )
  ) {
    console.error(
      `Item text property '${props.itemTextProperty}' is not available for all items.`
    );
  }
  if (
    props.itemTextProperty &&
    modelValue.value &&
    (typeof modelValue.value !== "object" ||
      (typeof modelValue.value === "object" &&
        modelValue.value !== null &&
        !(props.itemTextProperty in modelValue.value)))
  ) {
    console.error(
      `Item text property '${props.itemTextProperty}' is not available in the model.`
    );
  }

  if (
    props.itemDisabledProperty &&
    props.items?.some(
      (item) =>
        props.itemDisabledProperty &&
        typeof item === "object" &&
        item !== null &&
        props.itemDisabledProperty in item &&
        item[props.itemDisabledProperty] !== null &&
        typeof item[props.itemDisabledProperty] !== "boolean"
    )
  ) {
    console.error("Disabled value is not boolean in one of the items.");
  }
  if (
    props.itemDisabledProperty &&
    modelValue.value &&
    typeof modelValue.value === "object" &&
    modelValue.value !== null &&
    props.itemDisabledProperty in modelValue.value &&
    modelValue.value[props.itemDisabledProperty as keyof Object] !== null &&
    typeof modelValue.value[props.itemDisabledProperty as keyof Object] !== "boolean"
  ) {
    console.error("Disabled value is not boolean in model.");
  }

  if (
    props.itemReadonlyProperty &&
    props.items?.some(
      (item) =>
        props.itemReadonlyProperty &&
        (typeof item !== "object" ||
          (typeof item === "object" &&
            item !== null &&
            !(props.itemReadonlyProperty in item)))
    )
  ) {
    console.error(
      `Item read-only property '${props.itemReadonlyProperty}' is not available for all items.`
    );
  }
  if (
    props.itemReadonlyProperty &&
    props.items?.some(
      (item) =>
        props.itemReadonlyProperty &&
        typeof item === "object" &&
        item !== null &&
        props.itemReadonlyProperty in item &&
        item[props.itemReadonlyProperty] !== null &&
        typeof item[props.itemReadonlyProperty] !== "boolean"
    )
  ) {
    console.error("read-only value is not boolean in one of the items.");
  }
  if (
    props.itemReadonlyProperty &&
    modelValue.value &&
    (typeof modelValue.value !== "object" ||
      (typeof modelValue.value === "object" &&
        modelValue.value !== null &&
        !(props.itemReadonlyProperty in modelValue.value)))
  ) {
    console.error(
      `Item read-only property '${props.itemReadonlyProperty}' is not available in the model.`
    );
  }
  if (
    props.itemReadonlyProperty &&
    modelValue.value &&
    typeof modelValue.value === "object" &&
    modelValue.value !== null &&
    props.itemReadonlyProperty in modelValue.value &&
    modelValue.value[props.itemReadonlyProperty as keyof Object] !== null &&
    typeof modelValue.value[props.itemReadonlyProperty as keyof Object] !== "boolean"
  ) {
    console.error("read-only value is not boolean in model.");
  }
}

function onNumberOfVisibleItemsChanged(): void {
  setMaxHeight();
}

/** Watches the `selectedItem` attribute. */
function onSelectionChanged(): void {
  modelValue.value = selectedItem.value;
  emit("select", selectedItem.value);
  checkItemProperties();
}

function setMaxHeight(): void {
  if (
    !props.numberOfVisibleItems ||
    (props.items && props.numberOfVisibleItems >= props.items?.length)
  ) {
    listBoxRef.value?.style.removeProperty("maxHeight");
    return;
  }
  let maxHeight = 0;
  for (let i = 0; i < props.numberOfVisibleItems; i++) {
    maxHeight += listItemsRef.value[i].offsetHeight;
  }
  if (maxHeight > 0 && listBoxRef.value) {
    listBoxRef.value.style.maxHeight = `${maxHeight}px`;
  }
}

onMounted(() => {
  if (props.allowSelect && props.autoSelect) {
    selectedIdx.value = 0;
  }
  setMaxHeight();
  checkItemProperties();
});

watch(() => props.numberOfVisibleItems, onNumberOfVisibleItemsChanged);
watch(selectedItem, onSelectionChanged);

defineExpose({
  modelValue,
  selectedIdx,
  listBoxRef,
  listItemsRef,
  selectedItem,
  selectItem,
  moveSelection,
  removeSelection,
});
</script>

<template>
  <ul
    tabindex="0"
    :data-pak="pak"
    ref="listBoxRef"
    class="bissantzControls listBoxControl"
    :class="{
      bordered: bordered,
      divided: itemDivider,
      dense: dense,
      disabled: disabled,
      fullWidth: fullWidth,
      fullHeight: fullHeight,
      readonly: readonly,
      rounded: rounded,
      outlined: outlined,
      scroll: items && numberOfVisibleItems && numberOfVisibleItems < items.length,
    }"
    :data-design="design"
    @keydown.up.stop.prevent="moveSelection(-1)"
    @keydown.down.stop.prevent="moveSelection(1)"
    @keydown.esc.stop.prevent="removeSelection"
    @keydown.delete.stop.prevent="deleteItem"
  >
    <li
      ref="listItemsRef"
      class="listItem"
      v-for="(item, index) in items"
      :key="calculateKey(item) !== undefined ? calculateKey(item) : index"
      :class="{
        selected: allowSelect && selectedIdx === index,
        disabled: getDisabled(item),
        readonly: getReadonly(item),
        inactive: getInactive(item),
      }"
      @click="selectItem(index)"
    >
      <span class="label"
        ><slot
          name="list-item"
          :listItem="item"
          :index="index"
          :itemKey="calculateKey(item)"
          :itemText="calculateText(item)"
          >{{ calculateText(item) }}</slot
        ></span
      >
      <span
        v-if="allowOrder && !disabled && !getDisabled(item) && items && items.length > 1"
        @click.stop
        class="orderContainer"
      >
        <bc-button
          v-if="index > 0"
          class="orderButton"
          :design="design"
          tabindex="-1"
          outlined
          :rounded="rounded"
          @click="changeOrder($event, index, -1)"
        >
          &#8593;
        </bc-button>
        <bc-button
          v-if="index < items.length - 1"
          class="orderButton"
          :design="design"
          tabindex="-1"
          outlined
          :rounded="rounded"
          @click="changeOrder($event, index, 1)"
        >
          &#8595;
        </bc-button>
      </span>
    </li>
  </ul>
</template>

<style lang="less">
@import "../styles/components/bc-list-box.less";
</style>
